import * as React from 'react';
import {
    type FieldPath,
    type FieldValues,
    type UseFormReturn,
    useController,
    useFormContext,
    type ValidateResult,
    type ValidationRule,
} from 'react-hook-form';

import { assert } from '@gitbook/util-ts';
import { isHttpUrl, isUrl } from '@gitbook/util-url';

import { FieldControllerProvider } from './FieldControllerContext';
import { FieldGroup, type FieldGroupProps } from './FieldGroup';

type Validator = 'http-url' | 'url';

const validators: Record<Validator, (value: string) => ValidateResult> = {
    'http-url': (value) =>
        isHttpUrl(value) ||
        'Incorrect HTTP URL, enter a valid URL, starting with http:// or https://, and ensure there are no spaces or special characters.',
    url: (value) =>
        isUrl(value) ||
        'Invalid URL, enter a valid URL, and ensure there are no spaces or special characters.',
};

export type TextFieldProps<TFieldValues extends FieldValues> = Omit<
    FieldGroupProps,
    'name' | 'required' | 'maxLength' | 'minLength' | 'validate'
> & {
    form?: UseFormReturn<TFieldValues>;
    name: FieldPath<TFieldValues>;
    required?: boolean;
    maxLength?: number;
    minLength?: number;
    pattern?: string | ValidationRule<RegExp>;
    validate?: 'http-url' | 'url' | ((value: string) => ValidateResult);
};

/**
 * A field for text input.
 */
function TextFieldWithForwardRef<TFieldValues extends FieldValues>(
    props: TextFieldProps<TFieldValues>,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const form = useFormContext() ?? props.form;
    assert(form, 'TextField must be used within a Form');
    const controller = useController({
        control: form.control,
        name: props.name,
        rules: {
            required: props.required ? 'Required' : false,
            maxLength:
                props.maxLength !== undefined
                    ? {
                          value: props.maxLength,
                          message: `Maximum ${props.maxLength} character${props.maxLength > 1 ? 's' : ''}`,
                      }
                    : undefined,
            minLength:
                props.minLength !== undefined
                    ? {
                          value: props.minLength,
                          message: `Minimum ${props.minLength} character${props.minLength > 1 ? 's' : ''}`,
                      }
                    : undefined,
            pattern:
                typeof props.pattern === 'string'
                    ? {
                          value: new RegExp(props.pattern),
                          message: `Invalid format (${props.pattern})`,
                      }
                    : props.pattern,
            validate:
                typeof props.validate === 'function'
                    ? props.validate
                    : props.validate
                      ? validators[props.validate]
                      : undefined,
        },
    });
    return (
        <FieldControllerProvider controller={controller}>
            <FieldGroup ref={ref} className={props.className}>
                {props.children}
            </FieldGroup>
        </FieldControllerProvider>
    );
}

export const TextField = React.forwardRef(TextFieldWithForwardRef) as <
    TFieldValues extends FieldValues,
>(
    props: TextFieldProps<TFieldValues> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement;
